(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const liveRegion = document.querySelector('[data-live-region]');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const announce = (message) => {
    if (!liveRegion || !message) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 40);
  };
  const publishEvent = (name, payload = {}) => {
    try { window.Shopify?.analytics?.publish?.(name, payload); } catch { /* Analytics must never block commerce. */ }
  };
  window.Metasery = Object.assign(window.Metasery || {}, { publishEvent });

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shouldReduceMotion = () => reducedMotion.matches || document.body?.classList.contains('reduce-motion');
  const syncHeroVideoMotion = (scope = document) => {
    scope.querySelectorAll('[data-hero-video]').forEach((video) => {
      const button = video.closest('.hero-editorial__panel')?.querySelector('[data-hero-video-toggle]');
      if (shouldReduceMotion()) {
        video.pause();
        button?.classList.add('is-paused');
        button?.setAttribute('aria-label', 'Videoyu oynat');
      }
    });
  };
  syncHeroVideoMotion();
  reducedMotion.addEventListener?.('change', () => syncHeroVideoMotion());
  document.addEventListener('shopify:section:load', (event) => syncHeroVideoMotion(event.target));
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hero-video-toggle]');
    if (!button) return;
    const video = button.closest('.hero-editorial__panel')?.querySelector('[data-hero-video]');
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      button.classList.remove('is-paused');
      button.setAttribute('aria-label', 'Videoyu duraklat');
    } else {
      video.pause();
      button.classList.add('is-paused');
      button.setAttribute('aria-label', 'Videoyu oynat');
    }
  });

  const parseDocument = (html) => new DOMParser().parseFromString(html, 'text/html');
  // Pinning the body is the only lock iOS Safari respects, but it also resets the
  // scroll position, so the offset is stored and restored around it.
  let lockedScrollY = 0;
  const lockBody = (locked) => {
    const body = document.body;
    const alreadyLocked = body.classList.contains('is-locked');
    if (locked) {
      if (alreadyLocked) return;
      lockedScrollY = window.scrollY;
      body.style.top = `-${lockedScrollY}px`;
      body.classList.add('is-locked');
    } else {
      if (!alreadyLocked) return;
      body.classList.remove('is-locked');
      body.style.top = '';
      // Restore after the browser has re-laid out the unpinned document, or the
      // target offset can be clamped against a stale document height.
      window.requestAnimationFrame(() => window.scrollTo(0, lockedScrollY));
    }
  };
  // Shared so other bundles (the product specification panel) use this one lock.
  window.Metasery = Object.assign(window.Metasery || {}, { lockBody });
  const setInert = (elements, inert) => elements.filter(Boolean).forEach((element) => {
    if (inert) element.setAttribute('inert', '');
    else element.removeAttribute('inert');
  });

  class MQDrawer {
    constructor(drawer, overlay, inertElements = []) {
      this.drawer = drawer;
      this.overlay = overlay;
      this.inertElements = inertElements;
      this.lastFocus = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    open(trigger) {
      if (!this.drawer) return;
      document.querySelectorAll('[data-cart-drawer].is-open, [data-wishlist-drawer].is-open, [data-mobile-menu].is-open').forEach((openDrawer) => {
        if (openDrawer !== this.drawer) openDrawer.dispatchEvent(new CustomEvent('metasery:close'));
      });
      this.lastFocus = trigger || document.activeElement;
      this.drawer.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute?.('aria-expanded', 'true');
      setInert(this.inertElements, true);
      lockBody(true);
      document.addEventListener('keydown', this.onKeydown);
      this.drawer.querySelector(focusableSelector)?.focus();
    }

    close() {
      if (!this.drawer?.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.lastFocus?.setAttribute?.('aria-expanded', 'false');
      setInert(this.inertElements, false);
      lockBody(false);
      document.removeEventListener('keydown', this.onKeydown);
      this.lastFocus?.focus?.();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.drawer.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  /* ---- colour scheme -------------------------------------------------------
     The head script has already resolved and applied the scheme, so this only
     handles switching, persistence and keeping the labels honest. */
  (() => {
    const root = document.documentElement;
    const toggles = [...document.querySelectorAll('[data-scheme-toggle]')];
    if (!toggles.length) return;

    const strings = {
      light: window.Metasery?.strings?.schemeLight || 'Açık',
      dark: window.Metasery?.strings?.schemeDark || 'Koyu'
    };
    const meta = document.querySelector('[data-theme-color]');

    const sync = (scheme) => {
      root.setAttribute('data-theme', scheme);
      // The label names the scheme the button will switch TO, not the current one.
      const next = scheme === 'dark' ? strings.light : strings.dark;
      toggles.forEach((button) => {
        button.setAttribute('aria-pressed', String(scheme === 'dark'));
        button.querySelectorAll('[data-scheme-label]').forEach((label) => { label.textContent = next; });
      });
      if (meta) meta.setAttribute('content', scheme === 'dark' ? meta.dataset.dark : meta.dataset.light);
    };

    sync(root.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');

    toggles.forEach((button) => button.addEventListener('click', () => {
      const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      sync(next);
      try { window.localStorage.setItem('metasery:scheme', next); } catch (e) { /* private mode */ }
    }));
  })();

  const siteHeader = document.querySelector('[data-header]');
  if (siteHeader) {
    let ticking = false;
    const syncHeader = () => {
      siteHeader.classList.toggle('is-scrolled', window.scrollY > 40);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(syncHeader);
    }, { passive: true });
    syncHeader();
  }

  // <details> hides its own content the moment `open` flips, so both directions
  // are driven manually: open paints at 0fr then releases, close waits for the
  // grid-row transition before the element actually collapses.
  const motionOk = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    && !document.body.classList.contains('reduce-motion');
  document.querySelectorAll('.mobile-menu__level > summary, .faq-item > summary, .faq2__item > summary').forEach((summary) => {
    const details = summary.parentElement;
    const panel = details.querySelector(':scope > .mobile-menu__panel, :scope > .faq-item__panel, :scope > .faq2__panel');
    if (!panel) return;

    summary.addEventListener('click', (event) => {
      if (!motionOk()) return;
      if (!details.open) {
        details.classList.add('is-opening');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => details.classList.remove('is-opening'));
        });
        return;
      }
      event.preventDefault();
      details.classList.add('is-closing');
      let settled = false;
      const collapse = () => {
        if (settled) return;
        settled = true;
        details.open = false;
        details.classList.remove('is-closing');
      };
      panel.addEventListener('transitionend', collapse, { once: true });
      window.setTimeout(collapse, 420);
    });
  });

  const pageContent = [document.querySelector('.announcement'), document.querySelector('main'), document.querySelector('.site-footer')];
  const cartDrawer = new MQDrawer(document.querySelector('[data-cart-drawer]'), document.querySelector('[data-cart-overlay]'), [...pageContent, document.querySelector('.site-header')]);
  const wishlistDrawer = new MQDrawer(document.querySelector('[data-wishlist-drawer]'), document.querySelector('[data-wishlist-overlay]'), [...pageContent, document.querySelector('.site-header')]);
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const mobileDrawer = new MQDrawer(mobileMenu, document.querySelector('[data-mobile-menu-overlay]'), pageContent);
  cartDrawer.drawer?.addEventListener('metasery:close', () => cartDrawer.close());
  wishlistDrawer.drawer?.addEventListener('metasery:close', () => wishlistDrawer.close());
  mobileDrawer.drawer?.addEventListener('metasery:close', () => mobileDrawer.close());

  document.addEventListener('click', (event) => {
    const cartOpen = event.target.closest('[data-cart-open]');
    const wishlistOpen = event.target.closest('[data-wishlist-open]');
    const mobileOpen = event.target.closest('[data-mobile-menu-open]');
    if (cartOpen) cartDrawer.open(cartOpen);
    if (event.target.closest('[data-cart-close]') || event.target.matches('[data-cart-overlay]')) cartDrawer.close();
    if (wishlistOpen) { renderWishlist(); wishlistDrawer.open(wishlistOpen); }
    if (event.target.closest('[data-wishlist-close]') || event.target.matches('[data-wishlist-overlay]')) wishlistDrawer.close();
    if (mobileOpen) mobileDrawer.open(mobileOpen);
    if (event.target.closest('[data-mobile-menu-close]') || event.target.matches('[data-mobile-menu-overlay]')) mobileDrawer.close();
  });

  // Multiple top-level menu items can own independent editorial panels.
  const closeMegaMenus = (except) => {
    document.querySelectorAll('[data-mega-menu].is-open').forEach((menu) => {
      if (menu === except) return;
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.querySelector(`[data-mega-trigger="${CSS.escape(menu.dataset.megaMenu)}"]`)?.setAttribute('aria-expanded', 'false');
    });
  };
  document.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
    const menu = document.querySelector(`[data-mega-menu="${CSS.escape(trigger.dataset.megaTrigger)}"]`);
    if (!menu) return;
    const close = () => { menu.classList.remove('is-open'); menu.setAttribute('aria-hidden', 'true'); trigger.setAttribute('aria-expanded', 'false'); };
    const open = () => { closeMegaMenus(menu); menu.classList.add('is-open'); menu.setAttribute('aria-hidden', 'false'); trigger.setAttribute('aria-expanded', 'true'); };
    trigger.addEventListener('click', () => menu.classList.contains('is-open') ? close() : open());
    trigger.addEventListener('mouseenter', open);
    menu.addEventListener('mouseleave', close);
    trigger.closest('.site-header')?.addEventListener('mouseleave', close);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMegaMenus();
  });

  // Predictive search covers products and editorial content, with cancellation.
  const searchDialog = document.querySelector('[data-search-dialog]');
  const searchInput = searchDialog?.querySelector('[data-predictive-input]');
  const searchResults = searchDialog?.querySelector('[data-predictive-results]');
  const searchStatus = searchDialog?.querySelector('[data-predictive-status]');
  let searchTimer;
  let searchController;

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-search-open]')) {
      searchDialog?.showModal();
      searchInput?.focus();
    }
    if (event.target.closest('[data-search-close]')) searchDialog?.close();
  });

  const buildPredictiveCard = (item, type) => {
    const link = document.createElement('a');
    link.className = 'predictive-card';
    link.href = item.url;
    const imageSource = typeof item.image === 'string' ? item.image : item.image?.url || item.featured_image?.url;
    if (imageSource) {
      const image = document.createElement('img');
      image.src = imageSource;
      image.alt = '';
      image.width = 160;
      image.height = 160;
      image.loading = 'lazy';
      link.append(image);
    }
    const copy = document.createElement('span');
    const eyebrow = document.createElement('small');
    eyebrow.textContent = type;
    const title = document.createElement('strong');
    title.textContent = item.title;
    copy.append(eyebrow, title);
    if (item.price) {
      const price = document.createElement('small');
      price.textContent = item.price;
      copy.append(price);
    }
    link.append(copy);
    return link;
  };

  searchInput?.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    searchController?.abort();
    const query = searchInput.value.trim();
    if (query.length < 2) { searchResults.replaceChildren(); searchStatus.textContent = ''; return; }
    searchTimer = window.setTimeout(async () => {
      searchController = new AbortController();
      searchInput.setAttribute('aria-busy', 'true');
      try {
        const types = 'product,collection,page,article';
        const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=${types}&resources[limit]=6&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: searchController.signal });
        if (!response.ok) throw new Error('Arama tamamlanamadı');
        const groups = response.json ? (await response.json()).resources?.results || {} : {};
        const cards = [];
        Object.entries(groups).forEach(([type, items]) => (items || []).forEach((item) => cards.push(buildPredictiveCard(item, type.replace(/s$/, '')))));
        searchResults.replaceChildren(...cards);
        searchStatus.textContent = window.Metasery?.strings?.searchResults?.replace('[count]', String(cards.length)) || `${cards.length} results`;
        // Sonucsuz aramalar en degerli sinyal: karsilanmayan talebi gosterir.
        publishEvent('metasery:search_performed', { query, results: cards.length, no_results: cards.length === 0 });
      } catch (error) {
        if (error.name !== 'AbortError') { searchResults.replaceChildren(); searchStatus.textContent = window.Metasery?.strings?.searchError || ''; }
      } finally {
        searchInput.removeAttribute('aria-busy');
      }
    }, 180);
  });

  // Shopify Ajax cart with server-rendered sections and authoritative counts.
  const replaceSection = (id, html) => {
    if (!html) return;
    const current = document.getElementById(`shopify-section-${id}`);
    const next = parseDocument(html).getElementById(`shopify-section-${id}`);
    if (current && next) current.replaceWith(next);
  };
  const refreshCartReferences = () => {
    cartDrawer.drawer = document.querySelector('[data-cart-drawer]');
    cartDrawer.overlay = document.querySelector('[data-cart-overlay]');
    cartDrawer.drawer?.addEventListener('metasery:close', () => cartDrawer.close());
  };
  const getCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Sepet işlemi tamamlanamadı');
    return response.json();
  };

  document.addEventListener('submit', async (event) => {
    const addForm = event.target.closest('[data-ajax-add]');
    const changeForm = event.target.closest('[data-cart-change]');
    if (!addForm && !changeForm) return;
    event.preventDefault();
    const submitter = event.submitter;
    submitter?.setAttribute('aria-busy', 'true');
    submitter?.setAttribute('disabled', 'disabled');
    const drawerWasOpen = Boolean(cartDrawer.drawer?.classList.contains('is-open'));
    try {
      let response;
      if (addForm) {
        const formData = new FormData(addForm);
        formData.set('sections', 'cart-drawer');
        formData.set('sections_url', window.location.pathname);
        response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      } else {
        const formData = new FormData(changeForm);
        response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: formData.get('id'), quantity: Math.max(0, Number(formData.get('quantity'))), sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || window.Metasery?.strings?.cartError || 'Sepet hatası');
      replaceSection('cart-drawer', result.sections?.['cart-drawer']);
      refreshCartReferences();
      const cart = addForm ? await getCart() : result;
      document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = String(cart.item_count || 0); });
      announce(addForm ? window.Metasery?.strings?.added : window.Metasery?.strings?.cartUpdated);
      if (addForm) {
        publishEvent('metasery:add_to_cart', {
          variant_id: new FormData(addForm).get('id'),
          quantity: Number(new FormData(addForm).get('quantity') || 1),
          cart_item_count: cart.item_count || 0,
          source: addForm.closest('[data-quick-view-panel]') ? 'quick_view'
            : addForm.closest('.pcard') ? 'product_card' : 'product_page'
        });
      }
      if ((addForm && window.Metasery?.cartDrawer) || drawerWasOpen) cartDrawer.open(addForm ? submitter : null);
    } catch (error) {
      announce(error.message);
      const errorNode = (addForm || changeForm).querySelector('[data-form-error]');
      if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
    } finally {
      submitter?.removeAttribute('aria-busy');
      submitter?.removeAttribute('disabled');
    }
  });

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (!quantityButton) return;
    const form = quantityButton.closest('form');
    const input = form?.querySelector('input[name="quantity"]');
    if (!input) return;
    const max = Number(input.max) || Number.POSITIVE_INFINITY;
    input.value = String(Math.min(max, Math.max(0, Number(input.value) + Number(quantityButton.dataset.quantityChange))));
    form.requestSubmit(quantityButton);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-product-quantity-change]');
    if (!button) return;
    const input = button.closest('.quantity')?.querySelector('input[name="quantity"]');
    if (!input) return;
    const max = Number(input.max) || Number.POSITIVE_INFINITY;
    input.value = String(Math.min(max, Math.max(1, Number(input.value || 1) + Number(button.dataset.productQuantityChange))));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Creator videos only play while they are meaningfully visible.
  const observedVideos = document.querySelectorAll('[data-observed-video]');
  if (observedVideos.length && 'IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6 && !shouldReduceMotion()) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: [0, 0.6, 1] });
    observedVideos.forEach((video) => videoObserver.observe(video));
  }

  const filterDrawer = document.querySelector('[data-filter-drawer]');
  const filterOpen = document.querySelector('[data-filter-open]');
  const filterCloseButtons = document.querySelectorAll('[data-filter-close]');
  if (filterDrawer && filterOpen) {
    const filterInertElements = [
      document.querySelector('.announcement'),
      document.querySelector('.site-header'),
      document.querySelector('.site-footer'),
      document.querySelector('[data-cart-drawer]'),
      document.querySelector('[data-wishlist-drawer]')
    ];
    const trapFilterFocus = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeFilterDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...filterDrawer.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    const closeFilterDrawer = () => {
      if (!filterDrawer.classList.contains('is-open')) return;
      filterDrawer.classList.remove('is-open');
      filterDrawer.setAttribute('aria-hidden', 'true');
      setInert(filterInertElements, false);
      lockBody(false);
      filterOpen.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', trapFilterFocus);
      filterOpen.focus();
    };
    filterOpen.addEventListener('click', () => {
      filterDrawer.classList.add('is-open');
      filterDrawer.setAttribute('aria-hidden', 'false');
      setInert(filterInertElements, true);
      lockBody(true);
      filterOpen.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', trapFilterFocus);
      filterDrawer.querySelector(focusableSelector)?.focus();
    });
    filterCloseButtons.forEach((button) => button.addEventListener('click', closeFilterDrawer));
  }

  // Device-local wishlist. Only a compact, validated product reference is stored.
  const wishlistKey = 'metasery-wishlist-v1';
  try {
    const legacyWishlist = localStorage.getItem('metaquartz-wishlist-v1');
    if (!localStorage.getItem(wishlistKey) && legacyWishlist) localStorage.setItem(wishlistKey, legacyWishlist);
    localStorage.removeItem('metaquartz-wishlist-v1');
  } catch { /* Storage may be unavailable. */ }
  const isWishlistItem = (item) => item && typeof item === 'object' && typeof item.handle === 'string' && typeof item.title === 'string' && typeof item.url === 'string';
  const getWishlist = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(parsed) ? parsed.filter(isWishlistItem).slice(0, 60) : [];
    } catch { return []; }
  };
  const saveWishlist = (items) => {
    try { localStorage.setItem(wishlistKey, JSON.stringify(items.filter(isWishlistItem).slice(0, 60))); } catch { /* Storage may be unavailable. */ }
  };
  const updateWishlistButtons = () => {
    const handles = new Set(getWishlist().map((item) => item.handle));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = handles.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? window.Metasery?.strings?.removeWishlist : window.Metasery?.strings?.addWishlist);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((node) => { node.textContent = String(handles.size); });
  };
  const renderWishlist = () => {
    const container = document.querySelector('[data-wishlist-list]');
    if (!container) return;
    const items = getWishlist();
    if (!items.length) {
      const message = document.createElement('p');
      message.className = 'muted';
      message.textContent = window.Metasery?.strings?.wishlistEmpty || '';
      container.replaceChildren(message);
      return;
    }
    const cards = items.map((item) => {
      const card = document.createElement('article');
      card.className = 'wishlist-card';
      if (typeof item.image === 'string' && item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 220;
        image.height = 220;
        image.loading = 'lazy';
        card.append(image);
      }
      const copy = document.createElement('div');
      const title = document.createElement('h3');
      title.textContent = item.title;
      const price = document.createElement('p');
      price.textContent = typeof item.price === 'string' ? item.price : '';
      const actions = document.createElement('div');
      actions.className = 'wishlist-card__actions';
      const view = document.createElement('a');
      view.href = item.url;
      view.textContent = window.Metasery?.strings?.viewProduct || 'View';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.removeWishlist = item.handle;
      remove.textContent = window.Metasery?.strings?.remove || 'Kaldır';
      actions.append(view, remove);
      copy.append(title, price, actions);
      card.append(copy);
      return card;
    });
    container.replaceChildren(...cards);
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    const remove = event.target.closest('[data-remove-wishlist]');
    if (toggle) {
      const items = getWishlist();
      const index = items.findIndex((item) => item.handle === toggle.dataset.productHandle);
      if (index >= 0) items.splice(index, 1);
      else {
        items.unshift({ handle: toggle.dataset.productHandle, title: toggle.dataset.productTitle, url: toggle.dataset.productUrl, image: toggle.dataset.productImage || '', price: toggle.dataset.productPrice || '' });
        publishEvent('metasery:wishlist_added', { product_handle: toggle.dataset.productHandle });
      }
      saveWishlist(items);
      updateWishlistButtons();
      announce(index >= 0 ? window.Metasery?.strings?.removedWishlist : window.Metasery?.strings?.addedWishlist);
    }
    if (remove) {
      saveWishlist(getWishlist().filter((item) => item.handle !== remove.dataset.removeWishlist));
      renderWishlist();
      updateWishlistButtons();
    }
  });

  /* ---- quick view -----------------------------------------------------------
     The eye control on a product card opens a compact panel. The panel is rendered
     by Liquid through the Section Rendering API, so money formatting, variant
     availability and translations come from Shopify rather than being rebuilt in
     JS. Nothing is fetched until the shopper actually asks for it. */
  (() => {
    const dialog = document.querySelector('[data-quick-view-dialog]');
    const body = dialog?.querySelector('[data-quick-view-body]');
    if (!dialog || !body || typeof dialog.showModal !== 'function') return;

    let lastTrigger = null;
    const close = () => { if (dialog.open) dialog.close(); };

    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-quick-view]');
      if (!trigger) return;
      event.preventDefault();
      const handle = trigger.dataset.quickView;
      if (!handle) return;

      lastTrigger = trigger;
      trigger.setAttribute('aria-busy', 'true');
      try {
        const url = `${root}products/${handle}?section_id=quick-view`;
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(window.Metasery?.strings?.cartError || 'Hızlı bakış kullanılamıyor');
        body.innerHTML = await response.text();
        publishEvent('metasery:quick_view_opened', { product_handle: handle });
        dialog.showModal();
        lockBody(true);
        body.querySelector('a, button, select, input')?.focus();
      } catch (error) {
        // Sessizce urun sayfasina dus: kirik bir buton birakmaktan iyidir.
        window.location.href = `${root}products/${handle}`;
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    });

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-quick-view-close]') || event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      lockBody(false);
      body.innerHTML = '';
      lastTrigger?.focus();
      lastTrigger = null;
    });

    // Varyant secimi fiyati gunceller (secenek metninde zaten fiyat var).
    body.addEventListener('change', (event) => {
      const select = event.target.closest('[data-qv-variant]');
      if (!select) return;
      const chosen = select.options[select.selectedIndex];
      const submit = body.querySelector('.qv__submit');
      if (submit) submit.disabled = chosen.disabled;
    });
  })();

  /* ---- scroll reveal --------------------------------------------------------
     Anything with .reveal fades up once as it enters the viewport. The hidden
     state lives behind :root:not(.no-js) in CSS, so if this script never runs the
     content is simply visible. Elements are unobserved after firing: this is a
     one-way reveal, not a scroll effect that replays. */
  (() => {
    const targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    targets.forEach((el) => observer.observe(el));
  })();

  document.querySelectorAll('[data-localization-select]').forEach((select) => select.addEventListener('change', () => select.form.submit()));
  updateWishlistButtons();
})();
